import { routeExpression } from '../expression-routing/index.js';
import { decodeJwtPayload, loginWithGoogle } from '../auth/gma-auth.js';
import { fetchCourses, createCourseWork } from '../auth/google-classroom.js';

// ── PDF.js setup ──
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

// ── URL parameters ──
const urlParams = new URLSearchParams(window.location.search);
const pdfUrl = urlParams.get('pdf'); // auto-load PDF from URL
const viewMode = urlParams.get('mode') || 'work'; // 'check' or 'work'
const assignmentId = urlParams.get('assignmentId');

// ── DOM refs ──
const uploadArea = document.getElementById('upload-area');
const fileInput = document.getElementById('file-input');
const pdfContainer = document.getElementById('pdf-container');
const statsBar = document.getElementById('stats-bar');
const statsText = document.getElementById('stats-text');
const changeBtn = document.getElementById('change-btn');

// Track all detected equations and their solutions
// id -> { equation, hotspotEl, overlay, viewport, solutionState, restorableState, pageEquations }
const equationRegistry = new Map();

// Expose registry for Playwright test extraction (gated on ?test=1)
if (new URLSearchParams(location.search).has('test')) {
  window.__gmTestRegistry = equationRegistry;
}

// Store the current PDF file for uploading during assignment
let currentPdfFile = null;
let hostedPdfUrl = null; // set after upload to server

// OCR-detected equations (clean LaTeX, used for the linkout box when available)
let ocrEquationsList = null; // null = OCR hasn't run; [] = ran but found nothing

// ── File upload ──
uploadArea.addEventListener('click', () => fileInput.click());
changeBtn.addEventListener('click', () => {
  fileInput.value = '';
  fileInput.click();
});
fileInput.addEventListener('change', (e) => {
  if (e.target.files[0]) {
    currentPdfFile = e.target.files[0];
    hostedPdfUrl = null;
    loadPdf(e.target.files[0]);
  }
});

uploadArea.addEventListener('dragover', (e) => {
  e.preventDefault();
  uploadArea.classList.add('drag-over');
});
uploadArea.addEventListener('dragleave', () => {
  uploadArea.classList.remove('drag-over');
});
uploadArea.addEventListener('drop', (e) => {
  e.preventDefault();
  uploadArea.classList.remove('drag-over');
  const file = e.dataTransfer.files[0];
  if (file && file.type === 'application/pdf') {
    currentPdfFile = file;
    hostedPdfUrl = null;
    loadPdf(file);
  }
});

async function loadPdfFromUrl(url) {
  uploadArea.classList.add('hidden');
  document.getElementById('explainer').classList.add('hidden');
  changeBtn.classList.remove('hidden');
  pdfContainer.innerHTML = '<div class="loading-msg">Loading worksheet...</div>';
  try {
    // Use PDF.js directly with the URL — it handles CORS via its worker
    // and may succeed where fetch() would be blocked
    const pdf = await pdfjsLib.getDocument(url).promise;

    pdfContainer.innerHTML = '';
    statsBar.classList.add('hidden');
    equationRegistry.clear();
    let totalEquations = 0;

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const count = await renderPage(page);
      totalEquations += count;
    }

    // Answer-key pages often restate every problem; without dedup we'd show
    // two hotspots per equation. Keep the earliest (problem page), drop the
    // duplicates on later (answer-key) pages.
    const removed = dedupeAcrossPages();
    if (removed > 0) totalEquations -= removed;

    statsBar.classList.remove('hidden');
    statsText.innerHTML = `Found <strong>${totalEquations}</strong> equation${totalEquations !== 1 ? 's' : ''} in <strong>${pdf.numPages}</strong> page${pdf.numPages !== 1 ? 's' : ''}`;

    if (totalEquations === 0) {
      statsText.innerHTML +=
        ' &mdash; scanned PDF? Click <strong>Enhance with AI</strong> to detect equations.';
    }
    updateLinkoutBox();

    if (window.posthog) {
      posthog.capture('pdf_loaded', {
        pageId: 'pdf-viewer',
        source: 'url',
        page_count: pdf.numPages,
        equations_found: totalEquations,
      });
    }
  } catch (err) {
    pdfContainer.innerHTML = `<div class="loading-msg">Error loading PDF: ${err.message}</div>`;
    console.error('PDF URL load error:', err);
  }
}

// ── Upload PDF to server (for assignment links) ──
async function uploadPdfToServer(file) {
  const token = localStorage.getItem('gma-auth-token');
  if (!token) throw new Error('Please sign in first');

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const resp = await fetch(`${GMA_SERVER}/user-assets-via-data-url?prefix=pdf-`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'text/plain',
    },
    body: dataUrl,
  });
  console.log('[Upload] Response status:', resp.status);

  if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
  const url = (await resp.text()).trim();
  if (!url || url.startsWith('data:'))
    throw new Error('Upload did not return a valid URL');
  console.log('[Upload] Hosted URL:', url);

  // In dev, proxy CloudFront URLs through webpack dev server to avoid CORS
  if (
    window.location.hostname === 'localhost' &&
    url.includes('d1x7sz7o9o6442.cloudfront.net')
  ) {
    const proxyUrl = url.replace('https://d1x7sz7o9o6442.cloudfront.net', '/pdf-cdn');
    console.log('[Upload] Using proxy URL:', proxyUrl);
    return proxyUrl;
  }
  return url;
}

function loadGoogleScript(url) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${url}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = url;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${url}`));
    document.head.appendChild(s);
  });
}

// ── Auto-load from URL if ?pdf= is provided ──
if (pdfUrl) {
  loadPdfFromUrl(pdfUrl);
}

// ── Load & render PDF ──
async function loadPdf(file) {
  uploadArea.classList.add('hidden');
  document.getElementById('explainer').classList.add('hidden');
  changeBtn.classList.remove('hidden');
  pdfContainer.innerHTML = '<div class="loading-msg">Loading PDF...</div>';
  statsBar.classList.add('hidden');
  equationRegistry.clear();

  try {
    await loadPdfFromData(await file.arrayBuffer());
  } catch (err) {
    pdfContainer.innerHTML = `<div class="loading-msg">Error loading PDF: ${err.message}</div>`;
    console.error('PDF load error:', err);
  }
}

async function loadPdfFromData(arrayBuffer) {
  pdfContainer.innerHTML = '<div class="loading-msg">Loading PDF...</div>';
  statsBar.classList.add('hidden');
  equationRegistry.clear();

  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  pdfContainer.innerHTML = '';
  let totalEquations = 0;

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const count = await renderPage(page);
    totalEquations += count;
  }

  // See comment in loadPdf — dedup the answer-key repetitions.
  const removed = dedupeAcrossPages();
  if (removed > 0) totalEquations -= removed;

  statsBar.classList.remove('hidden');
  statsText.innerHTML = `Found <strong>${totalEquations}</strong> equation${totalEquations !== 1 ? 's' : ''} in <strong>${pdf.numPages}</strong> page${pdf.numPages !== 1 ? 's' : ''}`;

  if (totalEquations === 0) {
    statsText.innerHTML +=
      ' &mdash; try a worksheet with equations like <em>2x + 3 = 11</em>';
  }
  updateLinkoutBox();

  if (window.posthog) {
    posthog.capture('pdf_loaded', {
      pageId: 'pdf-viewer',
      source: 'file_upload',
      page_count: pdf.numPages,
      equations_found: totalEquations,
    });
  }
}

// ── Render one page ──
async function renderPage(page) {
  const scale = 2; // render at 2x for retina sharpness
  const viewport = page.getViewport({ scale });

  // Wrapper
  const wrapper = document.createElement('div');
  wrapper.className = 'page-wrapper';
  pdfContainer.appendChild(wrapper);

  // Canvas
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  wrapper.appendChild(canvas);

  await page.render({
    canvasContext: canvas.getContext('2d'),
    viewport,
  }).promise;

  // Store raw text content on wrapper for OCR matching later
  const textContent = await page.getTextContent();
  wrapper._textItems = textContent.items;
  wrapper._viewport = viewport;

  // Detect equations
  const equations = await detectEquations(page, viewport);

  if (equations.length > 0) {
    const overlay = document.createElement('div');
    overlay.className = 'equation-overlay';
    wrapper.appendChild(overlay);

    for (const eq of equations) {
      createHotspot(overlay, eq, viewport, equations);
    }
  }

  return equations.length;
}

// ══════════════════════════════════════════════════════
//  Equation Detection (Text-Layer Heuristics — v1)
// ══════════════════════════════════════════════════════

async function detectEquations(page, viewport) {
  const textContent = await page.getTextContent();
  const lines = groupByLine(textContent.items);
  const equations = [];

  // Page-level context: does this page tell students to Simplify / Factor /
  // Distribute / etc.? If so, we also accept bare expressions (no = sign).
  // Without this, kuta-distributive ("Simplify each expression"), kuta-
  // like-terms, and similar worksheets would detect 0 equations.
  const pageText = textContent.items.map((i) => i.str).join(' ');
  const acceptsBareExpressions =
    /\b(Simplify|Factor(?:ing)?|Expand|Distribute|Use the Distributive Property|Evaluate|Combine (?:Like )?Terms|Find the product|Multiply(?: each)?)\b/i.test(
      pageText
    );

  for (const line of lines) {
    // Sort items left-to-right
    line.items.sort((a, b) => a.transform[4] - b.transform[4]);

    // Split multi-column lines
    const columns = splitByColumns(line.items);

    for (const colItems of columns) {
      // Reconstruct equation text, handling super/subscripts and missing operators
      const reconstructed = reconstructEquation(colItems, line);
      if (!reconstructed || reconstructed.length < 3) continue;

      // Strip problem-number/letter prefix: "1)", "a.", "b.", "14)", etc.
      // Then strip common textbook prose directives (OpenStax etc.):
      //   "TRY IT : :2.63"  – call-out label with section number
      //   "Solve:"          – directive verb
      //   "Let a = -53"     – assignment-style intro (also "Leta=" if text layer collapsed the space)
      // Finally strip a trailing period that ends a sentence.
      const stripped = reconstructed
        .replace(/^\s*\d+\s*[.)]\s*/, '')
        .replace(/^\s*[a-z]\s*[.)]\s*/i, '')
        .replace(/^TRY\s*IT\s*:?\s*:?\s*\d+(?:\.\d+)?\s*/i, '')
        .replace(/^(?:Solve|Simplify|Evaluate|Find)\s*[:.]\s*/i, '')
        .replace(/^Let\s+([a-zA-Z])\s*=/, '$1 =') // "Let a =" -> "a ="
        .replace(/^Let([a-zA-Z])\s*=/, '$1 =') // "Leta=" (no space) -> "a ="
        .replace(/\s*\.\s*$/, '') // trailing sentence period
        .trim();

      // Accept any relation: equality, inequalities, not-equal. Inequalities
      // (both typeset ≤ ≥ ≠ and ASCII <= >= !=) route to whiteboard today but
      // at least stop being dropped entirely.
      const hasRelation = /[=<>\u2264\u2265\u2260]/.test(stripped);

      // For pages with a "Simplify / Factor / Distribute / Combine" directive
      // we also accept bare expressions (no relation). To avoid picking up
      // answer-key singletons like "-11r" (which are answers, not problems),
      // require EITHER multiple additive terms OR some internal structure
      // (parens, exponent, subscript, radical, fraction).
      const hasLetter = /[a-zA-Z]/.test(stripped);
      const hasStructure = /[()^_]|sqrt|\/.*[0-9a-zA-Z]/.test(stripped);
      const countTerms = (s) => {
        const noLeadingSign = s.replace(/^[\s+\-]+/, '');
        return noLeadingSign
          .split(/[+\-]/)
          .map((t) => t.trim())
          .filter(Boolean).length;
      };
      if (!hasRelation) {
        if (!acceptsBareExpressions) continue;
        if (countTerms(stripped) < 2 && !hasStructure) continue;
      }
      if (!hasLetter) continue;

      // Skip if it looks like prose (too many words)
      if (stripped.split(/\s+/).length > 8) continue;

      // Reject strings that contain prose-length words (5+ letters).
      // Math tokens are variables (1 char) or short function names:
      //   sqrt (4), log, ln, sin, cos, tan, sec, csc, cot (all ≤ 4 letters)
      // Anything longer (e.g. "Software", "Infinite", "Period") is a
      // worksheet header/label that snuck past the prefix stripper.
      if (/[a-zA-Z]{5,}/.test(stripped)) continue;

      // Explicit blocklist for 4-letter worksheet labels that slip past the
      // 5+ filter (e.g. "Name___________", "Date____", "Period___").
      // Using `(?![a-z])` instead of `\b` because `\b` is fooled when the
      // next char is `_` (a regex word character) — Kuta worksheets often
      // render "Name" immediately followed by underscores with no space.
      if (/^(?:Name|Date|Time|Page|Score|Class|Period)(?![a-z])/i.test(stripped))
        continue;

      // Convert fill-in-the-blank underscores to a GM NumberBox.
      // First clean up any dangling operators from text-layer artifacts,
      // then add the box as RHS of the equation.
      // e.g., "log_9(27) = _______" -> "log_9(27) = \numberbox"
      //       "log_2( 32)- = _______" -> "log_2(32) = \numberbox"
      let equation = stripped
        .replace(/_{3,}/g, '') // remove underscores
        .replace(/\(\s+/g, '(') // clean "( " -> "("
        .replace(/\s+\)/g, ')') // clean " )" -> ")"
        .replace(/\s*[+\-*/]\s*=\s*$/, ' = ') // fix "- =" -> " = " (dangling op before =)
        .replace(/\s*[+\-*/]\s*$/, '') // strip any remaining trailing operators
        .replace(/\s*=\s*$/, '') // strip trailing bare =
        .trim();

      // If the original had blanks (underscores), re-add as "= box" (GM PlaceHolder)
      // PlaceHolder is automatically tappable -- opens GM keyboard on tap.
      if (/_{3,}/.test(stripped) && equation) {
        equation += ' = \u2B1A';
      }

      // If the equation is empty after stripping blanks, skip it
      if (!equation || !equation.replace(/[^a-zA-Z0-9]/g, '')) continue;

      // Smart routing: detect equation type, use specialized exploration or whiteboard
      const encoded = encodeURIComponent(equation);
      const route = classifyAndRoute(equation, encoded);

      const bbox = computeBBox(colItems, viewport);
      equations.push({
        id: `eq-${equations.length}-${Date.now()}`,
        text: equation,
        bbox,
        route,
        allRoutes: [route],
      });
    }
  }

  return equations;
}

// ══════════════════════════════════════════════════════
//  Smart Equation Classification
// ══════════════════════════════════════════════════════

/**
 * Classify an equation and route it to the best exploration page.
 * Uses a priority system:
 *   1. Detect non-linear types that the linear router would misclassify
 *      (logarithmic, exponential, trigonometric) -> whiteboard
 *   2. Fall through to routeExpression() for linear algebra types
 *   3. Fall back to whiteboard if nothing matches
 */
function classifyAndRoute(equation, encoded) {
  const s = equation.toLowerCase();

  // ── Non-linear equation types ──
  // Detect these BEFORE the linear router, which would misclassify them.

  // ── Logarithmic equations ──
  // Match a variety of "log" forms:
  //   \blog     — word boundary before, catches `logx`, `log2`, `log7` (text
  //               layer often collapses the space between "log" and its arg)
  //   log_      — subscripted log (e.g., `log_2`)
  //   \\log     — LaTeX \log
  //   \bln\b    — natural log
  if (/\blog|log_|\\log|\bln\b/.test(s)) {
    // Log properties: has coefficient on log (2log, 3log), multiple log terms,
    // or operations between logs (log - log, log + log)
    // e.g., 2log(x+1) = 5, log x - log 3 = 8, log_2(x) + log_2(y)
    const logCount = (s.match(/log|\\log|ln\b/g) || []).length;
    const hasCoeffOnLog = /\d\s*\\?log/.test(s);
    const hasLogArithmetic = /log[^=]*[+-][^=]*log/.test(s);
    const isLogProperty = logCount >= 2 || hasCoeffOnLog || hasLogArithmetic;

    if (isLogProperty) {
      return {
        slug: 'log-properties',
        label: 'Log Properties',
        file: 'log-properties.html',
        url: `log-properties.html?eq=${encoded}&embedded=1`,
      };
    }

    // Simple log/exp form conversion: log_b(x) = y, or evaluating a single log
    return {
      slug: 'log-exponential-form',
      label: 'Log & Exponential Form',
      file: 'log-exponential-form.html',
      url: `log-exponential-form.html?eq=${encoded}&embedded=1`,
    };
  }

  // ── Exponential equations (variable in exponent) ──
  // e.g., 2^x = 8, 9^(2y) = 66, e^(3x-4) = 10, 25^x = 1/5
  // These can be solved by converting to log form -> log-exponential-form
  // But NOT quadratic (x^2) which the linear router handles correctly
  if (/\^/.test(s) && s.includes('=')) {
    const hasVarInExponent = /\w\s*\^\s*\(?[^)=]*[a-z]/i.test(s);
    const isQuadratic = /[a-z]\s*\^\s*2\b/.test(s) && !/\d\s*\^\s*\(?[^)]*[a-z]/.test(s);

    if (hasVarInExponent && !isQuadratic) {
      return {
        slug: 'log-exponential-form',
        label: 'Log & Exponential Form',
        file: 'log-exponential-form.html',
        url: `log-exponential-form.html?eq=${encoded}&embedded=1`,
      };
    }
  }

  // Trigonometric: contains sin, cos, tan, sec, csc, cot
  if (/\b(sin|cos|tan|sec|csc|cot)\b/.test(s)) {
    return makeWhiteboardRoute(equation, encoded, 'Trigonometric Equation');
  }

  // Radical/root: contains sqrt, nth root
  if (/\bsqrt\b|√/.test(s)) {
    return makeWhiteboardRoute(equation, encoded, 'Radical Equation');
  }

  // Absolute value
  if (/\|[^|]+\|/.test(s)) {
    return makeWhiteboardRoute(equation, encoded, 'Absolute Value Equation');
  }

  // ── Linear algebra types -> use routeExpression() ──
  const routes = routeExpression(equation);
  if (routes.length > 0) return routes[0];

  // ── Nothing matched -> whiteboard ──
  return makeWhiteboardRoute(equation, encoded, 'Whiteboard');
}

function makeWhiteboardRoute(equation, encoded, label) {
  return {
    slug: 'whiteboard',
    label,
    file: 'equation-whiteboard.html',
    url: `equation-whiteboard.html?eq=${encoded}`,
  };
}

/**
 * Reconstruct equation text from PDF text items, handling:
 * - Superscripts (smaller font, higher y) -> wrap with ^()
 * - Subscripts (smaller font, lower y) -> wrap with _
 * - Missing operators (empty-string items with width ~7) -> infer =, +, -
 */
function reconstructEquation(items, line) {
  if (items.length === 0) return '';

  // Determine the dominant (base) font size
  const fontSizes = items.map((i) => Math.abs(i.transform[0])).filter((s) => s > 0);
  if (fontSizes.length === 0) return '';
  const baseFontSize = mode(fontSizes);
  // Compute baseY from THIS column's items, not line.y. When two columns on
  // the same visual row have different baselines (e.g. Kuta problems where
  // one column has a stacked fraction and the other doesn't), line.y gets
  // averaged and doesn't match either column's actual baseline — which
  // breaks stacked-fraction pairing (above/below offsets use baseY).
  //
  // Pick the y where the column has the most total text WIDTH, among items
  // at baseFontSize. Baseline rows usually contain the longest content
  // (e.g. "= 7" is wider than a lone numerator or denominator).
  const yWeights = new Map();
  for (const it of items) {
    if (
      Math.abs(Math.abs(it.transform[0]) - baseFontSize) < 0.5 &&
      it.str &&
      it.str.trim()
    ) {
      const y = it.transform[5];
      yWeights.set(y, (yWeights.get(y) || 0) + (it.width || 1));
    }
  }
  let baseY = line.y;
  let bestW = -Infinity;
  for (const [y, w] of yWeights) {
    if (w > bestW) {
      bestW = w;
      baseY = y;
    }
  }

  // ── Detect stacked-fraction pairs ──
  // A stacked fraction has two text items at ~same font size and ~same x,
  // one above baseline (numerator) and one below (denominator). PDFs render
  // the fraction bar as a graphics operation, so it's absent from the text
  // layer. We pair them up and emit "(num)/(denom)" as a single frac token.
  const fracPairs = [];
  const usedIndexes = new Set();
  const baseSized = items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => {
      const fs = Math.abs(item.transform[0]);
      return fs >= baseFontSize * 0.75 && item.str && item.str.trim();
    });
  const above = baseSized.filter(({ item }) => item.transform[5] > baseY + 4);
  const below = baseSized.filter(({ item }) => item.transform[5] < baseY - 4);
  // Group adjacent items at similar y into numerator/denominator "runs".
  // A single fraction like 6/(5x) has a multi-char denominator that comes
  // through as two items ("5" then "x"); without grouping, we'd pair them
  // individually and miss the real denominator shape.
  const aboveGroups = groupAdjacentItems(above);
  const belowGroups = groupAdjacentItems(below);
  for (const ag of aboveGroups) {
    if (ag.used) continue;
    // Find the below group whose x-range overlaps most with this above group.
    let best = null,
      bestOverlap = 0;
    for (const bg of belowGroups) {
      if (bg.used) continue;
      const ov = Math.min(ag.xEnd, bg.xEnd) - Math.max(ag.xStart, bg.xStart);
      if (ov > bestOverlap) {
        bestOverlap = ov;
        best = bg;
      }
    }
    if (!best || bestOverlap <= 0) continue;
    ag.used = true;
    best.used = true;
    // Numerator/denominator may be PUA-encoded (Kuta). Decode before emit.
    const num = ag.items
      .map((e) => decodePuaString(e.item.str.trim()))
      .join('')
      .trim();
    const denom = best.items
      .map((e) => decodePuaString(e.item.str.trim()))
      .join('')
      .trim();
    fracPairs.push({
      num,
      denom,
      x: (ag.xStart + ag.xEnd) / 2,
      w: ag.xEnd - ag.xStart,
    });
    for (const e of ag.items) usedIndexes.add(e.idx);
    for (const e of best.items) usedIndexes.add(e.idx);
  }

  // Build tokens with position metadata for smarter assembly
  const tokens = [];
  for (let idx = 0; idx < items.length; idx++) {
    if (usedIndexes.has(idx)) continue; // consumed by a fraction pair
    const item = items[idx];
    const str = item.str;
    const fs = Math.abs(item.transform[0]);
    const y = item.transform[5];
    const x = item.transform[4];
    const w = item.width || 0;

    // PUA detection: single char (operator) or a run (inequality body etc.)
    const allPua =
      str &&
      str.length > 0 &&
      [...str].every((ch) => {
        const c = ch.charCodeAt(0);
        return c >= 0xe000 && c <= 0xf8ff;
      });
    // Truly-empty glyph of operator width (~5-10px) = missing-operator slot.
    // Do NOT match space characters; those are real spaces, not operators.
    const isEmptyOperator = str === '' && w >= 5 && w <= 10;

    if (allPua || isEmptyOperator) {
      // Many math PDF fonts offset standard ASCII into the PUA range:
      // code = 0xF000 + ASCII.
      // Decode the full run, not just the first char. Kuta inequality
      // worksheets encode entire expressions (e.g., "−12") as a single
      // multi-char PUA string.
      let decoded = '';
      if (allPua) {
        for (const ch of str) {
          const ascii = ch.charCodeAt(0) - 0xf000;
          decoded += ascii >= 0x20 && ascii <= 0x7e ? String.fromCharCode(ascii) : '';
        }
      }
      if (!decoded) decoded = '='; // empty-operator fallback (missing glyph slot)
      tokens.push({ type: 'op', text: decoded, x, w });
      continue;
    }

    if (!str || !str.trim()) continue;

    const isSuperSub = fs > 0 && fs < baseFontSize * 0.75;
    if (isSuperSub) {
      if (y > baseY + 2) {
        tokens.push({ type: 'sup', text: str.trim(), x, w });
      } else {
        tokens.push({ type: 'sub', text: str.trim(), x, w });
      }
    } else {
      tokens.push({ type: 'text', text: str, x, w });
    }
  }

  // Mix fraction tokens back in at their x positions
  for (const f of fracPairs) {
    tokens.push({ type: 'frac', num: f.num, denom: f.denom, x: f.x, w: f.w });
  }
  tokens.sort((a, b) => a.x - b.x);

  // Assemble tokens into equation string.
  // Key rule: subscripts attach to the PRECEDING text token (e.g., "log" + sub "4" -> "log_4")
  // Superscripts wrap with ^() (e.g., "3" + sup "x" -> "3^(x)")
  // Multi-token superscripts merge into a single ^() group
  // (e.g., sup "2" + sup "x" + sup "+ 2"  ->  "^(2x+2)")
  const parts = [];
  // Track the x-end of the most recent sub so we can detect a visible PDF gap
  // to the next baseline token. Without this, "log_5 7x" collapses to
  // "log_57x" which reads as "log base 57". We insert a space on gap > 3px.
  let lastSubEndX = null;
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    if (tok.type === 'frac') {
      // Only wrap in parens when the content isn't already a single atom.
      // "(1)/(x)" is just "1/x". "(6)/(5x)" still needs denom parens because
      // "6/5x" is ambiguous (could parse as (6/5)*x). Rule: wrap if the side
      // contains more than one character, OR has an operator/whitespace.
      // Only wrap when content is multi-atom:
      //   - any explicit operator / whitespace (e.g. `r- 4`, `x + 1`)
      //   - mixed letters + digits that form a product (e.g. `5x`, `2ab`)
      //   - exponent / subscript markers (e.g. `x^2`, `x_1`)
      // Single identifiers (`b`, `x`) and pure numbers (`18`, `3.5`) are
      // atoms and render without parens.
      const trimmed = (s) => String(s || '').trim();
      const isAtom = (s) => {
        const t = trimmed(s);
        if (!t) return true;
        // pure number (including decimals), or single identifier
        if (/^\d+(?:\.\d+)?$/.test(t)) return true;
        if (/^[a-zA-Z]$/.test(t)) return true;
        return false;
      };
      const wrap = (s) => (isAtom(s) ? trimmed(s) : `(${trimmed(s)})`);
      parts.push(`${wrap(tok.num)}/${wrap(tok.denom)}`);
      lastSubEndX = null;
    } else if (tok.type === 'sub') {
      // Attach subscript to previous text part by modifying it
      if (parts.length > 0) {
        const prev = parts[parts.length - 1];
        // If previous part ends with "(" or space, insert before the paren
        const m = prev.match(/^(.*?)\s*(\(.*)$/);
        if (m) {
          parts[parts.length - 1] = m[1] + '_' + tok.text + m[2];
        } else {
          parts[parts.length - 1] = prev + '_' + tok.text;
        }
      } else {
        parts.push('_' + tok.text);
      }
      lastSubEndX = tok.x + (tok.w || 0);
    } else if (tok.type === 'sup') {
      // Greedily merge consecutive sup tokens into a single exponent group.
      // PDFs often split "2x+3" as three separate sup tokens — without this
      // merge we'd produce ^(2)^(x)^(+3) which the parser rejects.
      let supText = tok.text;
      while (i + 1 < tokens.length && tokens[i + 1].type === 'sup') {
        i++;
        supText += tokens[i].text;
      }
      parts.push('^(' + supText.replace(/\s+/g, ' ').trim() + ')');
      lastSubEndX = null;
    } else {
      // text token — if we just had a sub and there's a visible x-gap (> 3px),
      // insert a space to preserve the PDF boundary.
      if (lastSubEndX !== null && tok.x - lastSubEndX > 3) {
        parts.push(' ');
      }
      parts.push(tok.text);
      lastSubEndX = null;
    }
  }

  const assembled = normalizeUnicodeMath(parts.join('').replace(/\s+/g, ' ').trim());
  return parenthesizeLogArgs(assembled);
}

/**
 * Normalize Unicode math characters to ASCII equivalents that the gmath
 * parser can tokenize. PDFs commonly use U+2212 MINUS SIGN, en/em dashes,
 * × and ÷ — the parser expects ASCII -, *, /.
 */
/**
 * Group {item, idx} entries into runs of items whose x-ranges are adjacent
 * (within `gap` px). Used to collect multi-character numerators or
 * denominators of a stacked fraction (e.g. "5" + "x" = "5x") before pairing
 * them with the opposite side.
 */
function groupAdjacentItems(entries, gap = 5) {
  const sorted = [...entries].sort((a, b) => a.item.transform[4] - b.item.transform[4]);
  const groups = [];
  for (const e of sorted) {
    const xs = e.item.transform[4];
    const xe = xs + (e.item.width || 0);
    const last = groups[groups.length - 1];
    if (last && xs - last.xEnd < gap) {
      last.items.push(e);
      last.xEnd = Math.max(last.xEnd, xe);
    } else {
      groups.push({ items: [e], xStart: xs, xEnd: xe });
    }
  }
  return groups;
}

/**
 * Decode a run of PUA-encoded characters (0xF000 + ASCII convention used by
 * Kuta/Type-3 math fonts) into plain ASCII. Non-PUA chars are passed through.
 * Used when we need the *text* of an item whose str is PUA-encoded, outside
 * the normal single-char operator path in reconstructEquation.
 */
function decodePuaString(s) {
  if (!s) return s;
  let out = '';
  for (const ch of s) {
    const c = ch.charCodeAt(0);
    if (c >= 0xe000 && c <= 0xf8ff) {
      const ascii = c - 0xf000;
      if (ascii >= 0x20 && ascii <= 0x7e) out += String.fromCharCode(ascii);
    } else {
      out += ch;
    }
  }
  return out;
}

function normalizeUnicodeMath(s) {
  return (
    s
      // NFKC collapses mathematical alphanumerics (𝑥 → x, 𝐴 → A, etc.) and
      // fancy spaces (U+00A0, U+2002-U+200A) to their ASCII equivalents.
      // This is critical for publishers (e.g. Effortless Math) that render
      // equation variables as U+1D465 instead of plain "x".
      .normalize('NFKC')
      .replace(/[\u2212\u2013\u2014]/g, '-') // minus sign, en dash, em dash -> -
      .replace(/\u00D7/g, '*') // × -> *
      .replace(/\u00F7/g, '/') // ÷ -> /
      .replace(/\u00B7/g, '*') // · (middle dot) -> *
      .replace(/\u2219/g, '*') // bullet operator -> *
      .replace(/\u22C5/g, '*') // ⋅ dot operator -> *
      .replace(/\u2032/g, "'")
  ); // prime
}

/** Return the most common value in an array */
function mode(arr) {
  const counts = {};
  let maxCount = 0,
    maxVal = arr[0];
  for (const v of arr) {
    counts[v] = (counts[v] || 0) + 1;
    if (counts[v] > maxCount) {
      maxCount = counts[v];
      maxVal = v;
    }
  }
  return maxVal;
}

/**
 * Group text items by approximate y-coordinate (same baseline = same line).
 * Tolerance 12 captures stacked-fraction numerators/denominators (same-font
 * text offset ~8-9px above/below baseline) while staying well clear of the
 * 90+ pixel gap between adjacent equation rows in typical worksheets.
 */
function groupByLine(items) {
  const lines = [];

  for (const item of items) {
    // Keep items with PUA chars or empty str with nonzero width (they may be operators)
    const hasPUA =
      item.str &&
      item.str.length === 1 &&
      item.str.charCodeAt(0) >= 0xe000 &&
      item.str.charCodeAt(0) <= 0xf8ff;
    if (!hasPUA && !item.str && !(item.width > 3)) continue;

    const y = item.transform[5];
    const fs = Math.abs(item.transform[0]) || 12;
    const isSmallFont = fs < 9; // likely a super/subscript

    let matched = false;
    for (const line of lines) {
      // Wider tolerance for small fonts (super/subscripts), moderate for baseline
      // text (to pull in stacked fraction numerators/denominators of the same size).
      const tol = isSmallFont ? 15 : 12;
      if (Math.abs(line.y - y) < tol) {
        line.items.push(item);
        // Only update y average from base-size items near the line's current y
        // (avoid pulling the line baseline toward fraction stacks).
        if (!isSmallFont && Math.abs(line.y - y) < 4) {
          line.y = (line.y * (line.items.length - 1) + y) / line.items.length;
        }
        matched = true;
        break;
      }
    }

    if (!matched) {
      lines.push({ y, items: [item] });
    }
  }

  return lines;
}

function splitByColumns(items) {
  if (items.length < 2) return [items];

  // Two kinds of breakpoints:
  //   * SPACER — an explicit wide-whitespace item (width > 50). The spacer
  //     item itself should be discarded (it's not content).
  //   * GAP    — a large x-gap between two content items with no spacer
  //     between them. The item AFTER the gap must be kept (it's content).
  const spacerBPs = new Set();
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const str = item.str;
    const w = item.width || 0;
    if ((!str || !str.trim()) && w > 50) {
      spacerBPs.add(i);
    }
  }

  const gapBPs = new Set();
  const gaps = [];
  for (let i = 1; i < items.length; i++) {
    if (spacerBPs.has(i) || spacerBPs.has(i - 1)) continue;
    const prevEnd = items[i - 1].transform[4] + (items[i - 1].width || 0);
    const currStart = items[i].transform[4];
    gaps.push({ index: i, size: currStart - prevEnd });
  }
  if (gaps.length > 0) {
    const sorted = gaps.map((g) => g.size).sort((a, b) => a - b);
    const medianGap = sorted[Math.floor(sorted.length / 2)] || 0;
    const threshold = Math.max(medianGap * 5, 40);
    for (const g of gaps) {
      if (g.size > threshold) gapBPs.add(g.index);
    }
  }

  if (spacerBPs.size === 0 && gapBPs.size === 0) return [items];

  // Combined boundaries. For each boundary we know whether to DROP the item
  // at the boundary (spacer) or KEEP it in the next group (gap).
  const boundaries = [
    ...[...spacerBPs].map((i) => ({ i, kind: 'spacer' })),
    ...[...gapBPs].map((i) => ({ i, kind: 'gap' })),
  ].sort((a, b) => a.i - b.i);

  const groups = [];
  let start = 0;
  for (const b of boundaries) {
    const end = b.kind === 'spacer' ? b.i : b.i; // exclusive end
    const slice = items.slice(start, end).filter((i) => {
      const w = i.width || 0;
      return !(!i.str || !i.str.trim()) || w <= 50;
    });
    if (slice.length > 0) groups.push(slice);
    start = b.kind === 'spacer' ? b.i + 1 : b.i;
  }
  const lastSlice = items.slice(start).filter((i) => {
    const w = i.width || 0;
    return !(!i.str || !i.str.trim()) || w <= 50;
  });
  if (lastSlice.length > 0) groups.push(lastSlice);

  return groups.length > 0 ? groups : [items];
}

function computeBBox(items, viewport) {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const item of items) {
    const x = item.transform[4];
    const y = item.transform[5];
    const fontSize = Math.abs(item.transform[0]) || 12;
    const w = item.width || fontSize * item.str.length * 0.6;

    const [vx1, vy1] = viewport.convertToViewportPoint(x, y);
    const [vx2, vy2] = viewport.convertToViewportPoint(x + w, y + fontSize);

    minX = Math.min(minX, vx1, vx2);
    minY = Math.min(minY, vy1, vy2);
    maxX = Math.max(maxX, vx1, vx2);
    maxY = Math.max(maxY, vy1, vy2);
  }

  const pad = 6;
  return {
    left: minX - pad,
    top: minY - pad,
    width: maxX - minX + pad * 2,
    height: maxY - minY + pad * 2,
  };
}

// ══════════════════════════════════════════════════════
//  Hotspot UI
// ══════════════════════════════════════════════════════

function createHotspot(overlay, equation, viewport, pageEquations) {
  const { bbox, route, text, id } = equation;
  const cw = viewport.width;
  const ch = viewport.height;

  const el = document.createElement('div');
  el.className = 'equation-hotspot';
  el.style.left = `${(bbox.left / cw) * 100}%`;
  el.style.top = `${(bbox.top / ch) * 100}%`;
  el.style.width = `${(bbox.width / cw) * 100}%`;
  el.style.height = `${(bbox.height / ch) * 100}%`;

  // Tooltip
  const tooltip = document.createElement('div');
  tooltip.className = 'eq-tooltip';
  tooltip.innerHTML = `
    <div><span class="eq-label">${route.label}</span>: ${escHtml(text)}</div>
    <div class="eq-action">Click to solve interactively</div>
  `;
  el.appendChild(tooltip);

  // Register this equation with all page equations (for gap calculation)
  equationRegistry.set(id, {
    equation,
    hotspotEl: el,
    overlay,
    viewport,
    pageEquations,
    solutionState: null,
    restorableState: null,
  });

  // Click -> open exploration in embedded modal
  el.addEventListener('click', () => {
    openExplorationModal(id);
  });

  overlay.appendChild(el);
}

// ══════════════════════════════════════════════════════
//  Exploration Modal (iframe)
// ══════════════════════════════════════════════════════

let activeModalEqId = null;
let latestSolutionState = null;

function openExplorationModal(eqId) {
  const reg = equationRegistry.get(eqId);
  if (!reg) return;
  const { equation } = reg;

  if (window.posthog) {
    posthog.capture('pdf_equation_opened', {
      pageId: 'pdf-viewer',
      equation: equation.text,
      route_label: equation.route?.label,
      route_slug: equation.route?.url
        ?.split('/')
        .pop()
        ?.split('?')[0]
        ?.replace('.html', ''),
    });
  }

  activeModalEqId = eqId;
  latestSolutionState = reg.solutionState;

  // Build URL with embedded flag
  const url = equation.route.url + '&embedded=1';

  // Create modal
  const backdrop = document.createElement('div');
  backdrop.className = 'explore-modal-backdrop';

  const modal = document.createElement('div');
  modal.className = 'explore-modal';

  // Header with editable equation
  const header = document.createElement('div');
  header.className = 'explore-modal-header';

  const headerLabel = document.createElement('h2');
  headerLabel.innerHTML = `${equation.route.label}:&nbsp;`;

  const eqInput = document.createElement('input');
  eqInput.type = 'text';
  eqInput.className = 'eq-text-input';
  eqInput.value = equation.text;
  eqInput.title = 'Edit equation \u2014 press Enter to reload';
  headerLabel.appendChild(eqInput);
  header.appendChild(headerLabel);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'explore-modal-close';
  const hasPriorWork =
    reg.solutionState?.solved ||
    reg.solutionState?.hasWork ||
    reg.solutionState?.steps?.length > 0;
  closeBtn.textContent = hasPriorWork ? 'Save & Close' : 'Close';
  if (hasPriorWork) closeBtn.classList.add('has-solution');
  header.appendChild(closeBtn);
  modal.appendChild(header);

  // Iframe
  const iframe = document.createElement('iframe');
  iframe.src = url;
  modal.appendChild(iframe);

  // Edit equation: press Enter to reload with corrected text
  eqInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const newText = eqInput.value.trim();
      if (!newText) return;

      // Re-classify and update the route
      const newEncoded = encodeURIComponent(newText);
      const newRoute = classifyAndRoute(newText, newEncoded);

      // Update the equation registry
      equation.text = newText;
      equation.route = newRoute;
      reg.restorableState = null; // clear old state since equation changed
      latestSolutionState = null;

      // Update header label
      headerLabel.childNodes[0].textContent = newRoute.label + ':\u00A0';

      // Reload iframe with new URL
      iframe.src = newRoute.url + '&embedded=1';

      // Reset close button
      closeBtn.textContent = 'Close';
      closeBtn.classList.remove('has-solution');

      // Update the tooltip on the hotspot
      const tooltip = reg.hotspotEl.querySelector('.eq-tooltip');
      if (tooltip) {
        tooltip.innerHTML = `
          <div><span class="eq-label">${newRoute.label}</span>: ${escHtml(newText)}</div>
          <div class="eq-action">Click to solve interactively</div>
        `;
      }
    }
  });

  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  document.body.style.overflow = 'hidden';

  // Update close button when solution arrives or work is done
  const updateCloseBtn = () => {
    if (
      latestSolutionState?.solved ||
      latestSolutionState?.hasWork ||
      latestSolutionState?.steps?.length > 0
    ) {
      closeBtn.textContent = 'Save & Close';
      closeBtn.classList.add('has-solution');
    }
  };

  // Listen for messages from iframe
  const onMessage = (e) => {
    if (e.data?.type === 'gm-solution-state') {
      latestSolutionState = e.data;
      updateCloseBtn();
    }
    // When iframe signals ready, restore previous state if we have one
    if (e.data?.type === 'gm-iframe-ready' && reg.restorableState) {
      iframe.contentWindow.postMessage(
        {
          type: 'gm-restore-state',
          state: reg.restorableState,
        },
        '*'
      );
    }
  };
  window.addEventListener('message', onMessage);

  // Close handler
  const close = () => {
    window.removeEventListener('message', onMessage);
    window.removeEventListener('keydown', onKey);

    // Save restorable state if available
    if (latestSolutionState?.restorableState) {
      reg.restorableState = latestSolutionState.restorableState;
    }

    // If we have solution data, annotate the PDF
    if (latestSolutionState && latestSolutionState.steps?.length > 0) {
      applySolutionAnnotation(eqId, latestSolutionState);

      if (window.posthog) {
        posthog.capture('pdf_equation_solved', {
          pageId: 'pdf-viewer',
          equation: equation.text,
          solved: !!latestSolutionState.solved,
          step_count: latestSolutionState.steps.length,
        });
      }
    }

    backdrop.remove();
    document.body.style.overflow = '';
    activeModalEqId = null;
  };

  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  const onKey = (e) => {
    if (e.key === 'Escape') close();
  };
  window.addEventListener('keydown', onKey);
}

// ══════════════════════════════════════════════════════
//  Solution Annotations
// ══════════════════════════════════════════════════════

/**
 * Compute available space for an annotation: both height and width.
 * Returns { height, width } in viewport pixels.
 */
function getAvailableSpace(eqId) {
  const reg = equationRegistry.get(eqId);
  if (!reg) return { height: 100, width: 300 };

  const { bbox } = reg.equation;
  const eqBottom = bbox.top + bbox.height;
  const eqLeft = bbox.left;
  const eqRight = bbox.left + bbox.width;

  let nearestBelow = Infinity;
  let nearestRight = Infinity;

  for (const other of reg.pageEquations) {
    if (other.id === eqId) continue;
    const otherBbox = other.bbox;
    const otherTop = otherBbox.top;
    const otherLeft = otherBbox.left;
    const otherCenterX = otherLeft + otherBbox.width / 2;
    const eqCenterX = eqLeft + bbox.width / 2;

    // Check equations BELOW in the same column (similar x center)
    if (
      otherTop > eqBottom &&
      Math.abs(otherCenterX - eqCenterX) < reg.viewport.width * 0.3
    ) {
      nearestBelow = Math.min(nearestBelow, otherTop);
    }

    // Check equations to the RIGHT on the same row (overlapping y range)
    // An equation is "to the right" if its left edge is past our right edge
    // and it's at a similar vertical position (within the annotation zone)
    if (
      otherLeft > eqRight &&
      otherTop < eqBottom + 200 &&
      otherTop + otherBbox.height > bbox.top
    ) {
      nearestRight = Math.min(nearestRight, otherLeft);
    }
  }

  if (nearestBelow === Infinity) {
    nearestBelow = reg.viewport.height - 20;
  }
  if (nearestRight === Infinity) {
    nearestRight = reg.viewport.width - 20;
  }

  return {
    height: Math.max(nearestBelow - eqBottom - 24, 30),
    width: Math.max(nearestRight - eqLeft - 12, 80),
  };
}

function applySolutionAnnotation(eqId, state) {
  const reg = equationRegistry.get(eqId);
  if (!reg) return;

  // Save state
  reg.solutionState = state;

  // Mark hotspot as checked/solved
  reg.hotspotEl.classList.add(state.solved ? 'solved' : 'checked');

  // Update tooltip
  const tooltip = reg.hotspotEl.querySelector('.eq-tooltip');
  if (tooltip) {
    const action = tooltip.querySelector('.eq-action');
    if (action) {
      action.textContent = state.solved
        ? 'Solved! Click to review'
        : 'Checked. Click to continue';
    }
  }

  // Remove existing annotation if any
  const existingAnnotation = reg.overlay.querySelector(`[data-eq-id="${eqId}"]`);
  if (existingAnnotation) existingAnnotation.remove();

  // ── Check mode: just show a small green checkmark ──
  if (viewMode === 'check') {
    const { bbox } = reg.equation;
    const cw = reg.viewport.width;
    const ch = reg.viewport.height;

    const checkmark = document.createElement('div');
    checkmark.className = 'check-annotation';
    checkmark.dataset.eqId = eqId;
    checkmark.textContent = '\u2713';
    checkmark.title = 'Checked with Graspable Math \u2014 click to review';

    // Position at the right edge of the equation
    const checkLeft = bbox.left + bbox.width + 8;
    const checkTop = bbox.top + bbox.height / 2 - 10;
    checkmark.style.left = `${(checkLeft / cw) * 100}%`;
    checkmark.style.top = `${(checkTop / ch) * 100}%`;

    // Click to reopen
    checkmark.addEventListener('click', () => openExplorationModal(eqId));

    reg.overlay.appendChild(checkmark);
    updateSolvedCount();
    return; // skip full annotation rendering
  }

  // Compute available space (both height and width) and scale font to fit
  const { bbox } = reg.equation;
  const cw = reg.viewport.width;
  const ch = reg.viewport.height;
  const available = getAvailableSpace(eqId);

  // Scale font to fit available gap. Each step needs ~1.6em of vertical space.
  // +1 for the badge/hint line which always renders.
  const numLines = state.steps.length + 1;
  // Deduct padding (6px top + 6px bottom = 12px display = 24 viewport-px)
  const maxFontFromGap = (available.height - 24) / (numLines * 1.6);
  // Cap at 90% of the equation's own font size, min 12px
  const fontSize = Math.max(12, Math.min(maxFontFromGap, bbox.height * 0.9));

  const annotation = document.createElement('div');
  annotation.className = 'solution-annotation';
  annotation.dataset.eqId = eqId;
  // The canvas renders at 2x scale but displays at container width (roughly half).
  // Convert viewport-pixel font size to display pixels.
  const displayFontSize = Math.round(fontSize / 2);
  annotation.style.fontSize = `${displayFontSize}px`;
  annotation.style.lineHeight = '1.6';

  // Position below the equation, constrained by both height and width
  const annotationTop = bbox.top + bbox.height + 4;
  annotation.style.left = `${(bbox.left / cw) * 100}%`;
  annotation.style.top = `${(annotationTop / ch) * 100}%`;
  annotation.style.maxHeight = `${((available.height - 8) / ch) * 100}%`;
  annotation.style.maxWidth = `${(available.width / cw) * 100}%`;

  // Build steps display
  let html = '';
  for (const step of state.steps) {
    html += `<div class="step">`;
    html += `<span class="step-arrow">\u2192</span>`;
    html += `<span class="step-eq">${escHtml(step.ascii)}</span>`;
    if (step.reason) {
      html += `<span class="step-reason">${escHtml(step.reason)}</span>`;
    }
    html += `</div>`;
  }
  if (state.solved && state.solution !== null && state.solution !== undefined) {
    html += `<span class="solution-badge">Solution: ${escHtml(String(state.solution))}</span>`;
  }
  if (state.solved) {
    html += ` <span class="reopen-hint">click to reopen</span>`;
  } else {
    html += `<div class="reopen-hint">Click to continue</div>`;
  }
  annotation.innerHTML = html;

  // Clicking the annotation reopens the exploration
  annotation.addEventListener('click', () => {
    openExplorationModal(eqId);
  });

  reg.overlay.appendChild(annotation);

  // Update stats
  updateSolvedCount();
}

function updateSolvedCount() {
  let worked = 0;
  for (const [, reg] of equationRegistry) {
    if (
      reg.solutionState?.solved ||
      reg.solutionState?.hasWork ||
      reg.solutionState?.steps?.length > 0
    )
      worked++;
  }
  const total = equationRegistry.size;
  if (worked > 0) {
    const pages = document.querySelectorAll('.page-wrapper').length;
    const label = viewMode === 'check' ? 'checked' : 'solved';
    statsText.innerHTML = `Found <strong>${total}</strong> equation${total !== 1 ? 's' : ''} in <strong>${pages}</strong> page${pages !== 1 ? 's' : ''} &mdash; <strong style="color:#4CAF50">${worked}</strong> ${label}`;
  }
}

/**
 * Populate the linkout box at the bottom of the page with all detected equations.
 * Each equation gets a clickable chip that opens the exploration or whiteboard.
 */
function updateLinkoutBox() {
  const box = document.getElementById('eq-linkout-box');
  const list = document.getElementById('eq-linkout-list');
  if (!box || !list) return;

  // If OCR has run, use OCR equations exclusively (clean LaTeX)
  if (ocrEquationsList && ocrEquationsList.length > 0) {
    box.classList.remove('hidden');
    list.innerHTML = '';

    for (const ocrEq of ocrEquationsList) {
      const encoded = encodeURIComponent(ocrEq);
      const route = classifyAndRoute(ocrEq, encoded);

      const chip = document.createElement('a');
      chip.className = 'eq-linkout-chip';
      chip.href = route.url;
      chip.target = '_blank';
      chip.title = `Open in ${route.label}`;

      const label = document.createElement('span');
      label.className = 'chip-label';
      label.textContent = route.label;
      chip.appendChild(label);

      const mathSpan = document.createElement('span');
      mathSpan.className = 'chip-math';
      try {
        if (window.katex) {
          katex.render(ocrEq, mathSpan, { throwOnError: false, displayMode: false });
        } else {
          mathSpan.textContent = ocrEq;
        }
      } catch (_) {
        mathSpan.textContent = ocrEq;
      }
      chip.appendChild(mathSpan);

      // Click → try to find a matching hotspot, or open in new tab
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        // Find best matching hotspot for this OCR equation
        const ocrTokens = extractKeyTokens(ocrEq);
        let bestId = null,
          bestScore = 0;
        for (const [id, reg] of equationRegistry) {
          const score = tokenOverlapScore(ocrTokens, extractKeyTokens(reg.equation.text));
          if (score > bestScore) {
            bestScore = score;
            bestId = id;
          }
        }
        if (bestId && bestScore > 0.2) {
          openExplorationModal(bestId);
        } else {
          window.open(route.url, '_blank');
        }
      });

      list.appendChild(chip);
    }
    return;
  }

  // Fallback: no OCR yet, use hotspot registry
  if (equationRegistry.size === 0) {
    box.classList.add('hidden');
    return;
  }

  box.classList.remove('hidden');
  list.innerHTML = '';

  for (const [id, reg] of equationRegistry) {
    const { equation } = reg;

    // Flag low-confidence text-layer results
    const t = equation.text;
    const hasDigit = /\d/.test(t);
    const hasMathFn = /log|ln\b|sin|cos|tan|sqrt/i.test(t);
    const stripFns = t
      .replace(/(log|ln|sin|cos|tan|sqrt)/gi, '')
      .replace(/[^a-zA-Z]/g, '');
    const tooManyLetters = stripFns.length > 5;
    const lowConfidence = (!hasDigit && !hasMathFn) || tooManyLetters || t.length < 4;

    const chip = document.createElement('a');
    chip.className = 'eq-linkout-chip' + (lowConfidence ? ' low-confidence' : '');
    chip.href = equation.route.url;
    chip.target = '_blank';
    chip.title = `Open in ${equation.route.label}`;

    // Label
    const label = document.createElement('span');
    label.className = 'chip-label';
    label.textContent = equation.route.label;
    chip.appendChild(label);

    // Render equation with KaTeX if available, fallback to text
    const mathSpan = document.createElement('span');
    mathSpan.className = 'chip-math';
    const eqText = equation.text;
    try {
      if (window.katex) {
        // Convert GM ASCII to LaTeX-ish for KaTeX
        const latex = eqText
          .replace(/⬚/g, '\\square')
          .replace(/_(\d+)/g, '_{$1}') // log_2 → log_{2}
          .replace(/\^(\([^)]+\))/g, '^{$1}') // ^(2y) → ^{(2y)}
          .replace(/\^(\d+)/g, '^{$1}'); // ^2 → ^{2}
        katex.render(latex, mathSpan, { throwOnError: false, displayMode: false });
      } else {
        mathSpan.textContent = eqText;
      }
    } catch (_) {
      mathSpan.textContent = eqText;
    }
    chip.appendChild(mathSpan);

    // Click → open in embedded modal
    chip.addEventListener('click', (e) => {
      e.preventDefault();
      openExplorationModal(id);
    });

    list.appendChild(chip);
  }
}

function escHtml(s) {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

// ══════════════════════════════════════════════════════
//  State Serialization (for sharing & submission)
// ══════════════════════════════════════════════════════

function captureFullState() {
  const equations = [];
  for (const [id, reg] of equationRegistry) {
    if (reg.solutionState) {
      equations.push({
        id,
        text: reg.equation.text,
        solutionState: reg.solutionState,
        restorableState: reg.restorableState,
      });
    }
  }
  return {
    v: 1,
    pdf: pdfUrl || null,
    mode: viewMode,
    equations,
  };
}

function generateShareLink() {
  const state = captureFullState();
  const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(state));
  const base = window.location.href.split('#')[0];
  return `${base}#state=${compressed}`;
}

// Restore state from URL hash on page load (after PDF is rendered)
function restoreFromUrlHash() {
  const hash = window.location.hash;
  if (!hash.startsWith('#state=')) return;

  const compressed = hash.slice(7); // skip '#state='
  try {
    const json = LZString.decompressFromEncodedURIComponent(compressed);
    if (!json) return;
    const state = JSON.parse(json);
    if (!state?.equations) return;

    console.log('[Restore] Found', state.equations.length, 'saved equations to restore');

    // Wait for equations to be detected, then apply saved states
    let attempts = 0;
    const applyInterval = setInterval(() => {
      if (equationRegistry.size === 0) {
        attempts++;
        if (attempts > 50) {
          // timeout after 10s
          clearInterval(applyInterval);
          console.warn('[Restore] Timed out waiting for equation detection');
        }
        return;
      }
      clearInterval(applyInterval);
      console.log(
        '[Restore] Registry has',
        equationRegistry.size,
        'equations, applying saved state'
      );

      const matchedIds = new Set();
      for (const saved of state.equations) {
        const savedTokens = extractKeyTokens(saved.text);

        // Find best match by token overlap (handles OCR vs text-layer text differences)
        let bestMatch = null;
        let bestScore = 0;
        for (const [id, reg] of equationRegistry) {
          if (matchedIds.has(id) || reg.solutionState) continue;
          const regTokens = extractKeyTokens(reg.equation.text);
          const score = tokenOverlapScore(savedTokens, regTokens);
          if (score > bestScore && score > 0.2) {
            bestScore = score;
            bestMatch = { id, reg };
          }
        }

        if (bestMatch) {
          matchedIds.add(bestMatch.id);
          const { id, reg } = bestMatch;
          // Update equation text to the saved (possibly OCR-cleaned) version
          reg.equation.text = saved.text;
          const encoded = encodeURIComponent(saved.text);
          reg.equation.route = classifyAndRoute(saved.text, encoded);
          if (saved.restorableState) reg.restorableState = saved.restorableState;
          if (saved.solutionState) {
            applySolutionAnnotation(id, saved.solutionState);
          }
          console.log(
            '[Restore] Matched "' +
              saved.text.substring(0, 30) +
              '" \u2192 hotspot (score: ' +
              Math.round(bestScore * 100) +
              '%)'
          );
        } else {
          console.log('[Restore] No match for "' + saved.text.substring(0, 30) + '"');
        }
      }
    }, 200);
  } catch (e) {
    console.warn('Failed to restore state from URL:', e);
  }
}

// Call restore after page is set up
restoreFromUrlHash();

// ══════════════════════════════════════════════════════
//  OCR Enhancement (V2 Detection)
// ══════════════════════════════════════════════════════

// In dev: proxied through webpack dev server -> activities.graspablemath.com/api
// In prod: use the actual API URL
const GMA_SERVER =
  window.location.hostname === 'localhost'
    ? '/gma-api'
    : 'https://activities.graspablemath.com/api';
const enhanceBtn = document.getElementById('enhance-btn');

// Show the enhance button after PDF is loaded (works even for scanned PDFs with 0 text-layer equations).
// If already logged in, auto-run OCR enhancement.
let ocrAutoTriggered = false;
new MutationObserver(() => {
  const hasPages = document.querySelectorAll('.page-wrapper').length > 0;
  if (hasPages && enhanceBtn.classList.contains('hidden')) {
    enhanceBtn.classList.remove('hidden');

    // Auto-enhance if already logged in
    if (!ocrAutoTriggered) {
      ocrAutoTriggered = true;
      // Check for activities backend token (set by math.new or our own auth)
      if (localStorage.getItem('gma-auth-token')) {
        setTimeout(() => runOcrEnhancement(true), 500);
      }
    }
  }
}).observe(pdfContainer, { childList: true, subtree: true });

/** Get a JWT for the activities backend by doing Google Sign-In. */
async function getActivitiesToken() {
  const CLIENT_ID =
    '81925747312-vao5h4l5bf2c8cbfssff1bejaut2at9n.apps.googleusercontent.com';
  await loadGoogleScript('https://accounts.google.com/gsi/client');

  // Get Google ID token via One Tap or prompt
  const googleCredential = await new Promise((resolve, reject) => {
    google.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: (response) => {
        if (response.credential) resolve(response.credential);
        else reject(new Error('No credential'));
      },
      use_fedcm_for_prompt: true,
    });
    google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        reject(new Error('Sign-in prompt was dismissed'));
      }
    });
  });

  // Exchange Google ID token for activities backend JWT
  const authResp = await fetch(`${GMA_SERVER}/authentication`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      strategy: 'google-id-token',
      access_token: googleCredential,
      createNew: true,
    }),
  });

  if (!authResp.ok) throw new Error('Authentication failed');
  const authData = await authResp.json();
  const token = authData.accessToken;
  if (token) localStorage.setItem('gma-auth-token', token);
  return token;
}

console.log('[pdf-viewer] attaching enhance-btn listener; btn exists:', !!enhanceBtn);
enhanceBtn.addEventListener('click', () => {
  console.log('[pdf-viewer] enhance-btn clicked!');
  runOcrEnhancement(false);
});

async function runOcrEnhancement(silent = false) {
  console.log('[OCR] runOcrEnhancement start, silent=', silent);
  if (window.posthog) {
    try {
      posthog.capture('pdf_ocr_triggered', {
        pageId: 'pdf-viewer',
        silent,
        page_count: document.querySelectorAll('.page-wrapper canvas').length,
      });
    } catch (e) {
      console.warn('[OCR] posthog error', e);
    }
  }

  enhanceBtn.disabled = true;
  enhanceBtn.textContent = silent ? 'Enhancing...' : 'Detecting...';
  console.log('[OCR] button set to', enhanceBtn.textContent);

  try {
    // If we already have a non-expired GMA token (gma-auth-token) — the only thing OCR
    // needs — skip the sign-in prompt.
    const existingActivitiesToken = localStorage.getItem('gma-auth-token');
    let activitiesTokenValid = false;
    if (existingActivitiesToken) {
      const p = decodeJwtPayload(existingActivitiesToken);
      activitiesTokenValid = !!p?.exp && p.exp > Date.now() / 1000 + 30;
    }

    if (!activitiesTokenValid) {
      if (silent) {
        // Don't prompt -- just show the button
        enhanceBtn.textContent = 'Enhance with AI';
        enhanceBtn.disabled = false;
        return;
      }
      enhanceBtn.textContent = 'Sign in to enhance...';
      const loginContainer = document.createElement('div');
      loginContainer.style.cssText =
        'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:1000;background:#fff;padding:30px;border-radius:12px;box-shadow:0 20px 60px rgba(0,0,0,0.3);text-align:center;';
      loginContainer.innerHTML =
        '<h3 style="margin-bottom:16px">Sign in to use AI detection</h3><div id="ocr-login-btn"></div><button onclick="this.parentElement.remove()" style="margin-top:12px;border:none;background:none;color:#888;cursor:pointer">Cancel</button>';
      document.body.appendChild(loginContainer);

      try {
        await loginWithGoogle(document.getElementById('ocr-login-btn'));
      } catch (_) {
        loginContainer.remove();
        enhanceBtn.textContent = 'Enhance with AI';
        enhanceBtn.disabled = false;
        return;
      }
      loginContainer.remove();
    }

    // The OCR endpoint is on the activities backend (activities.graspablemath.com).
    // It needs a JWT from that server, NOT the graspablemath.com token.
    // math.new stores this as 'gma-auth-token' in localStorage.
    let token = localStorage.getItem('gma-auth-token');

    // Quick sanity check: is the stored token expired? If yes, throw it out
    // so we re-exchange below.
    if (token) {
      const payload = decodeJwtPayload(token);
      if (!payload?.exp || payload.exp < Date.now() / 1000 + 30) {
        console.log('[OCR] stored gma-auth-token expired, refreshing');
        localStorage.removeItem('gma-auth-token');
        token = null;
      }
    }

    // If no token (or it's expired), do a fresh Google Sign-In -> exchange with activities backend
    if (!token) {
      token = await getActivitiesToken();
    }
    if (!token) throw new Error('Sign in at math.new first, then try again');

    // Process each page
    const pageCanvases = document.querySelectorAll('.page-wrapper canvas');
    console.log('[OCR] processing', pageCanvases.length, 'pages');
    let enhanced = 0;

    for (let i = 0; i < pageCanvases.length; i++) {
      enhanceBtn.textContent = `Page ${i + 1} of ${pageCanvases.length}...`;
      console.log('[OCR] page', i + 1, 'resizing canvas');
      const pageCanvas = pageCanvases[i];
      // Resize to max 1600px (matching math.new's MAX_IMAGE_DIMENSION)
      // and use PNG for lossless text clarity
      const imageDataUrl = resizeCanvasForOcr(pageCanvas);
      console.log('[OCR] page', i + 1, 'imageDataUrl length:', imageDataUrl?.length || 0);

      try {
        // Call OCR with ?raw=true to get MathPix's full response, including
        // `line_data` (per-equation bounding boxes). For scanned PDFs with no
        // text layer, this is the only way to place hotspots on the page.
        const response = await fetch(`${GMA_SERVER}/ocr/detect?raw=true`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ imageDataUrl }),
        });

        if (response.status === 401) {
          // Token is bad. Clear it and try to refresh once, then retry this page.
          console.log('[OCR] 401 — clearing stored token and re-exchanging');
          localStorage.removeItem('gma-auth-token');
          try {
            token = await getActivitiesToken();
          } catch (refreshErr) {
            console.error('[OCR] token refresh failed:', refreshErr);
            throw new Error('Sign in again (Sign out → Sign in at the top, then retry)');
          }
          if (!token)
            throw new Error('Sign in again (Sign out → Sign in at the top, then retry)');
          i--; // retry this page
          continue;
        }
        if (response.status === 429) throw new Error('Rate limit \u2014 try again later');
        if (!response.ok) throw new Error(`OCR failed: ${response.status}`);

        const data = await response.json();
        console.log('[OCR] Response keys:', Object.keys(data));
        const ocrLatex =
          data.latex_styled || data.latex || data.text || data.result || data.data?.latex;
        const rawLineData = data.line_data || [];

        // Compute scale factor to map MathPix image coords → page-canvas coords.
        // We sent a resized canvas (max 1600px); MathPix returns coords in that
        // resized image.
        const pageCanvas = pageCanvases[i];
        const scale = pageCanvas.width / getResizedCanvasWidth(pageCanvas);

        // Prefer line_data: each math entry has both text and bbox.
        const lineDataEquations = extractEquationsFromLineData(rawLineData, scale);
        console.log(
          '[OCR] line_data entries:',
          rawLineData.length,
          '/ math-extracted:',
          lineDataEquations.length
        );
        if (rawLineData.length > 0) {
          // Log the first few so we can see the actual shape MathPix returned
          console.log(
            '[OCR] first 3 line_data:',
            rawLineData.slice(0, 3).map((l) => ({
              type: l.type,
              subtype: l.subtype,
              has_cnt: !!l.cnt,
              cnt_len: l.cnt?.length,
              text: (l.text || l.text_display || '').slice(0, 80),
            }))
          );
        }

        // Parse the flattened LaTeX as a fallback (no positions).
        const latexEquations = ocrLatex ? parseOcrLatex(ocrLatex) : [];
        if (lineDataEquations.length === 0 && latexEquations.length === 0) {
          console.log(
            '[OCR] No equations found. Raw response:',
            JSON.stringify(data).slice(0, 500)
          );
          continue;
        }

        // Build the final list: use line_data entries when present (they have
        // bboxes); fall back to latex-only entries for any extras.
        const ocrEquations =
          lineDataEquations.length > 0
            ? lineDataEquations
            : latexEquations.map((text) => ({ text, bbox: null }));

        // Store text-only list for the linkout box (accumulate across pages)
        if (!ocrEquationsList) ocrEquationsList = [];
        ocrEquationsList.push(...ocrEquations.map((e) => e.text));

        // Update existing hotspots, add new ones from line_data bboxes
        const pageWrapper = pageCanvas.closest('.page-wrapper');
        const viewport = { width: pageCanvas.width, height: pageCanvas.height };
        enhancePageWithOcr(ocrEquations, pageWrapper, viewport);
        enhanced += ocrEquations.length;
      } catch (pageErr) {
        console.warn(`OCR failed for page ${i + 1}:`, pageErr);
        if (pageErr.message.includes('Rate limit')) {
          enhanceBtn.textContent = 'Rate limited \u2014 try later';
          break;
        }
      }
    }

    enhanceBtn.textContent =
      enhanced > 0 ? `Enhanced ${enhanced} equations` : 'No new equations found';
    enhanceBtn.disabled = true;
    updateLinkoutBox();

    if (window.posthog) {
      posthog.capture('pdf_ocr_completed', {
        pageId: 'pdf-viewer',
        equations_enhanced: enhanced,
      });
    }
    setTimeout(() => {
      enhanceBtn.textContent = 'Enhance with AI';
      enhanceBtn.disabled = false;
    }, 3000);
  } catch (err) {
    console.error('OCR enhancement failed:', err);
    enhanceBtn.textContent = err.message || 'Enhancement failed';
    enhanceBtn.disabled = false;
  }
}

/**
 * Compute the width we'd send to MathPix for a given canvas — used to scale
 * MathPix's returned coordinates back to canvas pixels.
 */
function getResizedCanvasWidth(sourceCanvas) {
  const MAX_DIM = 1600;
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  if (w <= MAX_DIM && h <= MAX_DIM) return w;
  const scale = Math.min(MAX_DIM / w, MAX_DIM / h);
  return Math.round(w * scale);
}

/**
 * Extract {text, bbox} per equation from MathPix's line_data.
 *
 * MathPix distinguishes two types for our purposes:
 *   type: 'math' — display-mode formula (e.g. answer blocks)
 *   type: 'text' — a paragraph-like entry that may contain one or more
 *                  inline math segments wrapped in `\( ... \)` or `$...$`
 *
 * Problem statements in typical worksheets are type='text' with `\(...\)`
 * math embedded — NOT type='math'. We accept both.
 *
 * Each entry has a `cnt` polygon (array of [x,y] corner points) in the
 * coordinate space of the image we sent. We convert to {left, top, width,
 * height} in page-canvas pixels by scaling by `scale` (pageCanvas.width /
 * resizedWidth).
 */
function extractEquationsFromLineData(lineData, scale) {
  if (!Array.isArray(lineData) || lineData.length === 0) return [];
  const equations = [];
  for (const line of lineData) {
    if (line.type !== 'math' && line.type !== 'text') continue;
    const raw = line.text || line.text_display || '';
    if (!raw.trim()) continue;

    // Pull out every math segment found in this entry — there's usually one
    // per line, but we don't assume that.
    const segments = extractLatexSegments(raw);
    if (segments.length === 0) continue;

    const bbox = cntToBbox(line.cnt, scale);
    if (!bbox) continue;

    for (const seg of segments) {
      // Strip MathPix prose wrappers that embed problem numbers / labels,
      // e.g. "\text{10)} 5(6+3r)+7 \geq 127" → "5(6+3r)+7 \geq 127".
      let cleaned = seg
        .replace(/\\text\s*\{[^}]*\}/g, '') // drop \text{...} entirely
        .replace(/^\s*\d+\s*[.)]\s*/, '') // strip leading "12) "
        .replace(/^\s*[a-z]\s*[.)]\s*/i, '') // strip leading "a) "
        .trim();
      if (!cleaned) continue;

      // Normalize LaTeX (\frac{a}{b} → (a)/(b), remove braces, etc.) then
      // wrap any bare log arguments. Reuse the same normalizer the OCR
      // linkout path uses to stay consistent.
      let ascii = parenthesizeLogArgs(cleaned);
      if (!ascii) continue;
      // Normalize LaTeX inequality macros to ASCII so the filter + parser
      // see a relation symbol. MathPix emits \leq, \geq, \neq, \lt, \gt.
      ascii = ascii
        .replace(/\\leq\b/g, '<=')
        .replace(/\\geq\b/g, '>=')
        .replace(/\\neq\b/g, '!=')
        .replace(/\\lt\b/g, '<')
        .replace(/\\gt\b/g, '>')
        .replace(/\s+/g, ' ')
        .trim();
      // Require a relation (equality OR inequality) — matches the text-layer
      // filter. Otherwise non-equation prose sneaks in.
      if (!/[=<>\u2264\u2265\u2260]|<=|>=|!=/.test(ascii)) continue;
      equations.push({ text: ascii, bbox });
    }
  }
  return equations;
}

/**
 * Return every LaTeX math segment found inside a string. MathPix delimits
 * inline math with `\(..\)`, display math with `\[..\]`, and textbook-style
 * with `$..$` or `$$..$$`. Bare math (`type: 'math'` entries) is returned
 * as-is after delimiter stripping.
 */
function extractLatexSegments(raw) {
  const segments = [];
  const delimited =
    /\\\(([\s\S]+?)\\\)|\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g;
  let m;
  let matchedAny = false;
  while ((m = delimited.exec(raw)) !== null) {
    matchedAny = true;
    const inner = (m[1] || m[2] || m[3] || m[4] || '').trim();
    if (inner) segments.push(inner);
  }
  if (!matchedAny) {
    // Treat the entire string as math (type='math' entries often arrive bare)
    // after stripping any leading/trailing delimiters we might have missed.
    const bare = raw
      .replace(/^\s*\\[(\[]\s*/, '')
      .replace(/\s*\\[)\]]\s*$/, '')
      .replace(/^\s*\$+\s*/, '')
      .replace(/\s*\$+\s*$/, '')
      .trim();
    if (bare) segments.push(bare);
  }
  return segments;
}

/**
 * Convert MathPix's `cnt` polygon (array of [x,y] points in image coords) to
 * a {left, top, width, height} bbox in page-canvas pixel space.
 */
function cntToBbox(cnt, scale) {
  if (!Array.isArray(cnt) || cnt.length === 0) return null;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const pt of cnt) {
    const [x, y] = pt;
    if (typeof x !== 'number' || typeof y !== 'number') continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!isFinite(minX)) return null;
  return {
    left: minX * scale,
    top: minY * scale,
    width: (maxX - minX) * scale,
    height: (maxY - minY) * scale,
  };
}

/** Resize a canvas to max 1600px for OCR (matching math.new). Returns PNG data URL. */
function resizeCanvasForOcr(sourceCanvas) {
  const MAX_DIM = 1600;
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;

  if (w <= MAX_DIM && h <= MAX_DIM) {
    return sourceCanvas.toDataURL('image/png');
  }

  const scale = Math.min(MAX_DIM / w, MAX_DIM / h);
  const resized = document.createElement('canvas');
  resized.width = Math.round(w * scale);
  resized.height = Math.round(h * scale);
  const ctx = resized.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, 0, resized.width, resized.height);
  return resized.toDataURL('image/png');
}

/**
 * Parse OCR LaTeX output into individual equations.
 * Simplified version of processDetectedMath from math-text.ts.
 */
function parseOcrLatex(latex) {
  // Strip environments
  let s = latex;
  s = s.replace(
    /\\begin{(aligned|alignat|array|gather|equation\*?|split|cases)}(\{[^}]*\})?/g,
    ''
  );
  s = s.replace(/\\end{(aligned|alignat|array|gather|equation\*?|split|cases)}/g, '');
  s = s.replace(/\\begin{tabular}(\{[^}]*\})?/g, '');
  s = s.replace(/\\end{tabular}/g, '');
  s = s.replace(/\\hline/g, '');

  // Extract math from markdown delimiters if present
  const mathBlocks = [];
  const mdPattern =
    /\\\[([\s\S]*?)\\\]|\$\$([\s\S]*?)\$\$|\\\(([\s\S]*?)\\\)|(?<!\$)\$(?!\$)((?:[^$\\]|\\.)+)\$(?!\$)/g;
  let hasDelimiters = false;
  let m;
  while ((m = mdPattern.exec(s)) !== null) {
    hasDelimiters = true;
    mathBlocks.push(m[1] || m[2] || m[3] || m[4]);
  }

  const segments = hasDelimiters ? mathBlocks : [s];
  const equations = [];

  for (const segment of segments) {
    // Split on \\ line breaks
    const lines = segment
      .split('\\\\')
      .map((l) => {
        // Clean each line
        return l
          .replace(/\\displaystyle/g, '')
          .replace(/\\[,;!]/g, '')
          .replace(/\\quad/g, ' ')
          .replace(/\\qquad/g, ' ')
          .replace(/&/g, '')
          .replace(/\\text\{[^}]*\}/g, '') // strip \text{}
          .replace(/[.,\s]+$/, '')
          .trim();
      })
      .filter((l) => l && !/^[\s_\\{}]*$/.test(l));

    for (const line of lines) {
      // Must have an = sign and a variable to be an equation
      if (!line.includes('=') && !/[a-zA-Z]/.test(line)) continue;

      // Handle trailing = -> add placeholder
      const withPlaceholder = line.replace(/=\s*$/, '= \u2B1A');

      // Parenthesize log arguments: log_b XY + Z → log_b(XY) + Z
      // Textbook convention: log binds over multiplication but not addition
      const withLogParens = parenthesizeLogArgs(withPlaceholder);

      equations.push(withLogParens);
    }
  }

  return equations;
}

/**
 * Normalize LaTeX to GM-friendly ASCII format.
 * Handles \log, \frac, \sqrt, subscripts, superscripts, etc.
 */
function normalizeLatexToAscii(eq) {
  let s = eq;

  // Handle \frac BEFORE stripping braces (it needs the {}{} structure)
  s = s.replace(/\\frac\s*\{([^}]+)\}\s*\{([^}]+)\}/g, '($1)/($2)');
  s = s.replace(/\\sqrt\s*\{([^}]+)\}/g, 'sqrt($1)');

  // Strip LaTeX command prefixes
  s = s.replace(/\\log/g, 'log');
  s = s.replace(/\\ln/g, 'ln');
  s = s.replace(/\\cdot/g, '*');
  s = s.replace(/\\times/g, '*');
  s = s.replace(/\\div/g, '/');
  s = s.replace(/\\left\s*|\\right\s*/g, '');
  s = s.replace(/\\operatorname\{([^}]+)\}/g, '$1');

  // Remove spaces before subscripts/superscripts (MathPix often adds them)
  // e.g., "log _{4}" → "log_{4}", "x ^{2}" → "x^{2}"
  s = s.replace(/\s+_/g, '_');
  s = s.replace(/\s+\^/g, '^');

  // Normalize subscripts: _{8} → _8 (but only simple ones, not \frac inside)
  s = s.replace(/_\{(\d+)\}/g, '_$1');
  s = s.replace(/_\{([a-zA-Z])\}/g, '_$1');
  // Complex subscripts like _{\frac{1}{3}} — leave as-is or simplify
  s = s.replace(/_\{([^}]+)\}/g, '_($1)');

  // Normalize superscripts: ^{2} → ^(2)
  s = s.replace(/\^\{([^}]+)\}/g, '^($1)');

  // Strip remaining simple braces
  s = s.replace(/\{([^{}]+)\}/g, '$1');
  s = s.replace(/\{([^{}]+)\}/g, '$1');

  // Clean up whitespace
  s = s.replace(/\s+/g, ' ').trim();

  return s;
}

/**
 * Add explicit parentheses around log/ln arguments when they're not already
 * parenthesized. Follows textbook convention where log binds over multiplication
 * but stops at +/- and =.
 *
 * Only operates on GM ASCII (run normalizeLatexToAscii first).
 * Skips expressions where log already has parenthesized arguments.
 */
function parenthesizeLogArgs(eq) {
  // First normalize LaTeX to GM ASCII (no-op for already-ASCII input)
  let s = normalizeLatexToAscii(eq);

  // Parenthesize bare log arguments. Argument:
  //   - starts with a non-operator character
  //   - may contain balanced (...) groups (e.g., x^(2))
  //   - terminates before +, -, =, or end of string
  // Textbook convention: log binds over multiplication but stops at +/-/=.
  s = s.replace(
    /(?<=^|[^a-zA-Z])(log(?:_\d+|_\([^)]+\))?|ln)\s+(?!\()([^+\-=,\s)](?:\([^)]*\)|[^+\-=)])*?)(?=\s*(?:[+\-=]|$))/g,
    (match, fn, arg) => {
      const trimmedArg = arg.trim();
      if (!trimmedArg) return match;
      return `${fn}(${trimmedArg})`;
    }
  );

  return s;
}

/**
 * Post-detection dedup across pages. Kuta and similar worksheets put an
 * answer key on later pages that repeats every problem statement; without
 * this pass we'd show two hotspots per equation. We iterate hotspots in
 * DOM order (= page order) and keep only the first occurrence of each
 * normalized text. Returns the number of hotspots removed.
 */
function dedupeAcrossPages() {
  const normalize = (t) =>
    (t || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^\w=<>+\-*/().]/g, '');
  const seen = new Map(); // normalized text → first id we saw
  const hotspots = document.querySelectorAll('.equation-hotspot');
  // Build a DOM→registry map once
  const byEl = new Map();
  for (const [id, reg] of equationRegistry) byEl.set(reg.hotspotEl, id);

  let removed = 0;
  for (const el of hotspots) {
    const id = byEl.get(el);
    if (!id) continue;
    const reg = equationRegistry.get(id);
    if (!reg) continue;
    const key = normalize(reg.equation.text);
    if (!key) continue; // skip empty normalizations
    if (seen.has(key)) {
      el.remove();
      equationRegistry.delete(id);
      removed++;
    } else {
      seen.set(key, id);
    }
  }
  if (removed > 0)
    console.log(`[Dedup] Removed ${removed} duplicate hotspots across pages`);
  return removed;
}

/**
 * Intersection-over-union-ish overlap between two bboxes in the same
 * coordinate space. Returns a number in [0, 1] where 1 means the OCR bbox
 * sits entirely within an existing hotspot's bbox (or vice versa).
 */
function bboxOverlap(a, b) {
  if (!a || !b) return 0;
  const x1 = Math.max(a.left, b.left);
  const y1 = Math.max(a.top, b.top);
  const x2 = Math.min(a.left + a.width, b.left + b.width);
  const y2 = Math.min(a.top + a.height, b.top + b.height);
  if (x2 <= x1 || y2 <= y1) return 0;
  const inter = (x2 - x1) * (y2 - y1);
  const smaller = Math.min(a.width * a.height, b.width * b.height);
  return smaller > 0 ? inter / smaller : 0;
}

/**
 * Enhance a page's hotspots with OCR results.
 * Accepts either an array of strings (legacy) or [{text, bbox?}] objects from
 * MathPix line_data. When `bbox` is provided, new hotspots are created at
 * those positions — critical for scanned PDFs where the text layer is empty.
 * For PDFs that DO have text-layer hotspots, OCR text updates the existing
 * hotspot via token-overlap matching.
 */
function enhancePageWithOcr(ocrEquations, pageWrapper, viewport) {
  if (!ocrEquations.length || !pageWrapper) return;

  // Normalize shape: accept both string[] and {text, bbox?}[]
  const normalized = ocrEquations.map((e) =>
    typeof e === 'string' ? { text: e, bbox: null } : e
  );
  console.log('[OCR Enhance] Got', normalized.length, 'equations', normalized);

  // For scanned PDFs the text-layer pass never created an overlay. Make one
  // now so OCR-detected equations have somewhere to land.
  let overlay = pageWrapper.querySelector('.equation-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'equation-overlay';
    pageWrapper.appendChild(overlay);
    console.log('[OCR Enhance] Created missing overlay for this page');
  }

  // Get all hotspots on this page
  const pageHotspots = [];
  for (const [id, reg] of equationRegistry) {
    if (overlay.contains(reg.hotspotEl)) {
      pageHotspots.push({ id, reg });
    }
  }
  console.log('[OCR Enhance] Page has', pageHotspots.length, 'existing hotspots');

  const matchedHotspotIds = new Set();

  for (const { text: ocrEq, bbox: lineDataBbox } of normalized) {
    // Prefer BBOX-based matching when MathPix gave us a position for this
    // equation. Token overlap is unreliable on inequality-heavy pages where
    // many problems share digits (6, 7, etc.) and can pair with the wrong
    // text-layer hotspot, overwriting its text.
    let bestMatch = null;
    let bestScore = 0;

    if (lineDataBbox) {
      for (const { id, reg } of pageHotspots) {
        if (matchedHotspotIds.has(id)) continue;
        const overlap = bboxOverlap(lineDataBbox, reg.equation.bbox);
        if (overlap > bestScore && overlap > 0.3) {
          bestScore = overlap;
          bestMatch = { id, reg };
        }
      }
    }

    // Fall back to token overlap only when no bbox info is available.
    if (!bestMatch && !lineDataBbox) {
      const ocrVars = extractKeyTokens(ocrEq);
      for (const { id, reg } of pageHotspots) {
        if (matchedHotspotIds.has(id)) continue;
        const hotspotVars = extractKeyTokens(reg.equation.text);
        const score = tokenOverlapScore(ocrVars, hotspotVars);
        if (score > bestScore && score > 0.2) {
          bestScore = score;
          bestMatch = { id, reg };
        }
      }
    }

    console.log(
      `[OCR Enhance] "${ocrEq.substring(0, 40)}" \u2192 best match: "${bestMatch?.reg?.equation?.text?.substring(0, 40) || 'NONE'}" (score: ${Math.round(bestScore * 100)}%)`
    );

    if (bestMatch) {
      matchedHotspotIds.add(bestMatch.id);
      const { reg } = bestMatch;

      // Update equation text with the cleaner OCR version
      const oldText = reg.equation.text;
      reg.equation.text = ocrEq;
      const encoded = encodeURIComponent(ocrEq);
      reg.equation.route = classifyAndRoute(ocrEq, encoded);

      const tooltip = reg.hotspotEl.querySelector('.eq-tooltip');
      if (tooltip) {
        tooltip.innerHTML = `
          <div><span class="eq-label">${reg.equation.route.label}</span>: ${escHtml(ocrEq)}</div>
          <div class="eq-action">Click to solve interactively</div>
        `;
      }
      console.log(`[OCR Enhance] Updated "${oldText}" \u2192 "${ocrEq}"`);
    } else {
      // No hotspot match. Prefer bbox from line_data (works for scanned PDFs);
      // fall back to searching the text layer (works for text PDFs where OCR
      // found an equation we missed).
      let bbox = lineDataBbox;
      if (!bbox) {
        const textItems = pageWrapper._textItems;
        const vp = pageWrapper._viewport || viewport;
        if (textItems) {
          bbox = findTextLayerPosition(ocrEq, textItems, vp);
        }
      }
      if (!bbox) {
        console.log(
          `[OCR Enhance] No bbox for "${ocrEq.substring(0, 40)}" — skipping hotspot`
        );
        continue;
      }

      let activeOverlay = overlay;
      if (!activeOverlay) {
        activeOverlay = document.createElement('div');
        activeOverlay.className = 'equation-overlay';
        pageWrapper.appendChild(activeOverlay);
      }

      const encoded = encodeURIComponent(ocrEq);
      const route = classifyAndRoute(ocrEq, encoded);
      const eq = {
        id: `ocr-new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        text: ocrEq,
        bbox,
        route,
        allRoutes: [route],
      };
      const pageEqs = pageHotspots.map((h) => h.reg.equation);
      createHotspot(activeOverlay, eq, viewport, [...pageEqs, eq]);
      console.log(
        `[OCR Enhance] Created new hotspot for "${ocrEq.substring(0, 40)}" at`,
        bbox
      );
    }
  }
}

/**
 * Search the raw text-layer items for a region that likely corresponds
 * to an OCR-detected equation. Uses key tokens (numbers, variables) to
 * find text-layer lines that share content with the OCR equation.
 */
function findTextLayerPosition(ocrEq, textItems, viewport) {
  const ocrTokens = extractKeyTokens(ocrEq);
  if (ocrTokens.size === 0) return null;

  // Group text items by approximate y (same line)
  const lineMap = new Map();
  for (const item of textItems) {
    if (!item.str) continue;
    const y = Math.round(item.transform[5] / 5) * 5; // bucket by 5
    if (!lineMap.has(y)) lineMap.set(y, []);
    lineMap.get(y).push(item);
  }

  // Score each line by token overlap with the OCR equation
  let bestLine = null;
  let bestScore = 0;

  for (const [y, items] of lineMap) {
    // Decode PUA chars in the line text
    const lineText = items
      .map((i) => {
        if (i.str && i.str.length === 1 && i.str.charCodeAt(0) >= 0xe000) {
          return String.fromCharCode(i.str.charCodeAt(0) - 0xf000);
        }
        return i.str || '';
      })
      .join(' ');

    const lineTokens = extractKeyTokens(lineText);
    const score = tokenOverlapScore(ocrTokens, lineTokens);

    if (score > bestScore && score > 0.25) {
      bestScore = score;
      bestLine = { y, items };
    }
  }

  if (!bestLine) return null;

  // Compute bounding box from the matched line's items
  return computeBBox(bestLine.items, viewport);
}

/** Extract key tokens (variables, numbers, function names) for matching. */
function extractKeyTokens(eq) {
  // Normalize LaTeX
  const s = eq
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1 $2')
    .replace(/\\sqrt\{([^}]+)\}/g, 'sqrt $1')
    .replace(/\\left|\\right|\\cdot|\\times|\\div/g, '')
    .replace(/[\\{}()\[\]\u2B1A\u25A1]/g, ' ');
  // Extract tokens: numbers, variable letters, function names
  const tokens = s.match(/\d+|[a-zA-Z]+/g) || [];
  return new Set(tokens.map((t) => t.toLowerCase()));
}

/** Score based on token overlap (Jaccard-like). */
function tokenOverlapScore(setA, setB) {
  if (!setA.size || !setB.size) return 0;
  let overlap = 0;
  for (const t of setA) if (setB.has(t)) overlap++;
  return overlap / Math.max(setA.size, setB.size);
}

// ══════════════════════════════════════════════════════
//  Assign to Class (Phase 2)
// ══════════════════════════════════════════════════════

const assignBtn = document.getElementById('assign-btn');
const turninBtn = document.getElementById('turnin-btn');

// Show Assign button when a PDF is loaded (teacher flow)
// Show Turn In button when in assignment mode (student flow)
function updateActionButtons() {
  if (equationRegistry.size > 0) {
    if (assignmentId) {
      turninBtn.classList.remove('hidden');
    } else {
      assignBtn.classList.remove('hidden');
    }
  }
}

// Use MutationObserver to detect when equations appear and show action buttons
new MutationObserver(() => updateActionButtons()).observe(pdfContainer, {
  childList: true,
  subtree: true,
});

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}
function defaultDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7); // 1 week from now
  return d.toISOString().slice(0, 10);
}

assignBtn.addEventListener('click', openAssignModal);

async function openAssignModal() {
  const backdrop = document.createElement('div');
  backdrop.className = 'assign-modal-backdrop';

  const modal = document.createElement('div');
  modal.className = 'assign-modal';
  modal.innerHTML = `
    <h2>Assign to Google Classroom</h2>

    <label>Course</label>
    <select id="assign-course">
      <option value="">Loading courses...</option>
    </select>

    <label>Assignment Title</label>
    <input type="text" id="assign-title" value="Math Worksheet" placeholder="e.g., Chapter 4 Practice">

    <label>Due Date</label>
    <input type="date" id="assign-due-date" value="${defaultDueDate()}" min="${todayDate()}">

    <label>PDF Source</label>
    <input type="text" id="assign-pdf-url" value="${escHtml(hostedPdfUrl || pdfUrl || (currentPdfFile ? '(will upload automatically)' : ''))}" placeholder="https://..." ${currentPdfFile ? 'disabled' : ''}>

    <label>Mode</label>
    <div class="mode-options">
      <div class="mode-option selected" data-mode="check">
        <div class="mode-icon">\u2713</div>
        <div class="mode-label">Check Mode</div>
        <div class="mode-desc">Students work on paper, use GM to check</div>
      </div>
      <div class="mode-option" data-mode="work">
        <div class="mode-icon">\u270E</div>
        <div class="mode-label">GM Work Mode</div>
        <div class="mode-desc">Students solve entirely in GM</div>
      </div>
    </div>

    <div class="status-msg" id="assign-status"></div>

    <div class="modal-actions">
      <button class="btn-cancel">Cancel</button>
      <button class="btn-primary" id="assign-submit" disabled>Assign</button>
    </div>
  `;

  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);

  let selectedMode = 'check';
  const courseSelect = modal.querySelector('#assign-course');
  const submitBtn = modal.querySelector('#assign-submit');
  const statusMsg = modal.querySelector('#assign-status');
  const cancelBtn = modal.querySelector('.btn-cancel');

  // Mode toggle
  modal.querySelectorAll('.mode-option').forEach((opt) => {
    opt.addEventListener('click', () => {
      modal
        .querySelectorAll('.mode-option')
        .forEach((o) => o.classList.remove('selected'));
      opt.classList.add('selected');
      selectedMode = opt.dataset.mode;
    });
  });

  // Close
  const close = () => backdrop.remove();
  cancelBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  // Load courses
  try {
    const courses = await fetchCourses();
    courseSelect.innerHTML = courses.length
      ? courses
          .map(
            (c) =>
              `<option value="${c.id}">${escHtml(c.name)}${c.section ? ' - ' + escHtml(c.section) : ''}</option>`
          )
          .join('')
      : '<option value="">No active courses found</option>';
    submitBtn.disabled = courses.length === 0;
  } catch (err) {
    courseSelect.innerHTML = '<option value="">Failed to load courses</option>';
    statusMsg.textContent = err.message;
    statusMsg.className = 'status-msg error';
  }

  // Submit
  submitBtn.addEventListener('click', async () => {
    const courseId = courseSelect.value;
    const title = modal.querySelector('#assign-title').value.trim();

    if (!courseId || !title) {
      statusMsg.textContent = 'Please fill in all fields.';
      statusMsg.className = 'status-msg error';
      return;
    }

    submitBtn.disabled = true;

    try {
      // Step 1: Upload the PDF to get a hosted URL
      const manualUrl = modal.querySelector('#assign-pdf-url').value.trim();
      // Ignore placeholder text
      let assignPdfUrl =
        hostedPdfUrl || (manualUrl && !manualUrl.startsWith('(') ? manualUrl : null);

      if (!assignPdfUrl && currentPdfFile) {
        statusMsg.textContent = 'Uploading worksheet...';
        statusMsg.className = 'status-msg';
        assignPdfUrl = await uploadPdfToServer(currentPdfFile);
        hostedPdfUrl = assignPdfUrl; // cache for re-use
      }

      if (!assignPdfUrl) {
        statusMsg.textContent = 'No PDF to assign. Please load a worksheet first.';
        statusMsg.className = 'status-msg error';
        submitBtn.disabled = false;
        return;
      }

      // Step 2: Create the Classroom assignment
      statusMsg.textContent = 'Creating assignment...';
      statusMsg.className = 'status-msg';

      const studentUrl = new URL('pdf-viewer.html', window.location.href);
      studentUrl.searchParams.set('pdf', assignPdfUrl);
      studentUrl.searchParams.set('mode', selectedMode);
      studentUrl.searchParams.set('assignmentId', 'classroom');

      // Parse due date
      const dueDateStr = modal.querySelector('#assign-due-date').value;
      let dueDate = null;
      if (dueDateStr) {
        const [year, month, day] = dueDateStr.split('-').map(Number);
        dueDate = { year, month, day };
      }

      await createCourseWork(courseId, {
        title,
        description: `Open this worksheet in Graspable Math (${selectedMode === 'check' ? 'Check Mode' : 'GM Work Mode'}).`,
        url: studentUrl.toString(),
        dueDate,
      });

      console.log('[Assign] Student URL:', studentUrl.toString());
      statusMsg.textContent = 'Assignment created!';
      statusMsg.className = 'status-msg success';
      setTimeout(close, 1500);
    } catch (err) {
      statusMsg.textContent = 'Error: ' + err.message;
      statusMsg.className = 'status-msg error';
      submitBtn.disabled = false;
    }
  });
}

// ══════════════════════════════════════════════════════
//  Turn In (Phase 3)
// ══════════════════════════════════════════════════════

turninBtn.addEventListener('click', async () => {
  if (window.posthog) {
    let solved = 0;
    for (const reg of equationRegistry.values()) {
      if (reg.solutionState?.solved || reg.solutionState?.steps?.length > 0) solved++;
    }
    posthog.capture('pdf_turned_in', {
      pageId: 'pdf-viewer',
      equations_total: equationRegistry.size,
      equations_solved: solved,
    });
  }

  // Generate the share link with all work state
  const shareLink = generateShareLink();

  turninBtn.disabled = true;
  turninBtn.textContent = 'Submitting...';

  // Copy link to clipboard as fallback
  try {
    await navigator.clipboard.writeText(shareLink);
  } catch (_) {}

  // Open Google Classroom's share dialog with the link pre-filled
  // This lets the student attach the link as a submission
  const classroomShareUrl = `https://classroom.google.com/share?url=${encodeURIComponent(shareLink)}&title=${encodeURIComponent('My worksheet work')}`;
  window.open(classroomShareUrl, 'classroom-share', 'width=600,height=500');

  turninBtn.textContent = 'Turned In';
  turninBtn.style.background = '#888';
});
